import React, { useEffect, useState } from "react";
import axios from "axios";
import { 
  Save, 
  Plus, 
  Trash2, 
  RotateCcw, 
  ToggleLeft, 
  ToggleRight,
  Type,
  Image as ImageIcon,
  Link as LinkIcon,
  List
} from "lucide-react";

const AdminFeatureTwo = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  
  const [formData, setFormData] = useState({
    isActive: true,
    mainImage: "",
    shapeImage: "",
    subtitle: "",
    title: "",
    description: "",
    listItems: [],
    button: {},
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://allyoutube01-2d46d9aff565.herokuapp.com/api/feature-two-section");
      setFormData(res.data.data);
      setMessage({ type: "", text: "" });
    } catch (error) {
      setMessage({ type: "danger", text: "Failed to load data." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Handlers ---

  // 1. Simple flat state changes
  const setSimpleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // 2. Button handler
  const handleButtonChange = (field, value) => {
    setFormData({
      ...formData,
      button: { ...formData.button, [field]: value },
    });
  };

  // 3. List Items Array Handlers
  const handleListItemChange = (index, field, value) => {
    const updatedItems = [...formData.listItems];
    updatedItems[index][field] = value;
    setFormData({ ...formData, listItems: updatedItems });
  };

  const addListItem = () => {
    setFormData({
      ...formData,
      listItems: [
        ...formData.listItems,
        {
          id: `l_${Date.now()}`,
          text: "New Feature Tool",
        },
      ],
    });
  };

  const removeListItem = (index) => {
    if (window.confirm("Delete this item?")) {
      const updatedItems = formData.listItems.filter((_, i) => i !== index);
      setFormData({ ...formData, listItems: updatedItems });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put("https://allyoutube01-2d46d9aff565.herokuapp.com/api/feature-two-section", formData);
      setMessage({ type: "success", text: "Feature Two updated successfully!" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error(error);
      setMessage({ type: "danger", text: "Failed to update." });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm("Reset all settings to default?")) {
      try {
        await axios.post("https://allyoutube01-2d46d9aff565.herokuapp.com/api/feature-two-section/reset");
        fetchData();
        setMessage({ type: "success", text: "Reset to defaults." });
      } catch (error) {
        setMessage({ type: "danger", text: "Failed to reset." });
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading Admin...</div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">Manage Feature</h3>
        <div className="d-flex gap-2">
            <button 
                className={`btn btn-sm ${formData.isActive ? 'btn-success' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                onClick={() => setSimpleChange('isActive', !formData.isActive)}
            >
                {formData.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                {formData.isActive ? "Active" : "Disabled"}
            </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" onClick={handleReset}>
            <RotateCcw size={18} /> Reset
          </button>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={handleSubmit} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} mb-4`}>
          {message.text}
          <button type="button" className="btn-close float-end" onClick={() => setMessage({type: '', text: ''})}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          
          {/* --- 1. Text Content --- */}
          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-dark fw-bold py-3 d-flex align-items-center gap-2">
                    <Type size={20} /> Text Content
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <label className="form-label text-dark small fw-bold">Subtitle</label>
                        <input type="text" className="form-control" value={formData.subtitle} onChange={e => setSimpleChange('subtitle', e.target.value)} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label text-dark small fw-bold">Main Title</label>
                        <textarea className="form-control" rows="2" value={formData.title} onChange={e => setSimpleChange('title', e.target.value)} />
                    </div>
                    <div className="mb-0">
                        <label className="form-label text-dark small fw-bold">Description</label>
                        <textarea className="form-control" rows="4" value={formData.description} onChange={e => setSimpleChange('description', e.target.value)} />
                    </div>
                </div>
            </div>
          </div>

          {/* --- 2. Images & Button --- */}
          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-dark fw-bold py-3 d-flex align-items-center gap-2">
                    <ImageIcon size={20} /> Media & Button
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <label className="form-label text-dark small fw-bold">Main Image</label>
                        <input type="text" className="form-control form-control-sm mb-2" value={formData.mainImage} onChange={e => setSimpleChange('mainImage', e.target.value)} />
                        {formData.mainImage && <img src={formData.mainImage} className="img-fluid bg-light p-1 rounded border" style={{maxHeight:'100px'}} alt="Preview"/>}
                    </div>
                    <div className="mb-3">
                        <label className="form-label text-dark small fw-bold">Shape Image</label>
                        <input type="text" className="form-control form-control-sm" value={formData.shapeImage} onChange={e => setSimpleChange('shapeImage', e.target.value)} />
                    </div>
                    <hr className="my-3"/>
                    <div className="row g-2">
                        <div className="col-6">
                            <label className="form-label text-dark small">Button Text</label>
                            <input type="text" className="form-control form-control-sm" value={formData.button.text} onChange={e => handleButtonChange('text', e.target.value)} />
                        </div>
                        <div className="col-6">
                            <label className="form-label text-dark small">Button Link</label>
                            <input type="text" className="form-control form-control-sm" value={formData.button.link} onChange={e => handleButtonChange('link', e.target.value)} />
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* --- 3. List Items Manager --- */}
          <div className="col-12 mb-4">
            <div className="card shadow-sm">
                <div className="card-header bg-dark fw-bold py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <List size={20} /> Feature List Items
                    </div>
                    <button className="btn btn-sm btn-primary" onClick={addListItem}><Plus size={16} /> Add Item</button>
                </div>
                <div className="card-body">
                    <p className="text-muted small mb-2">
                        Note: The frontend splits the list into two columns (3 items each). Ensure you have 6 items for the best layout.
                    </p>
                    {formData.listItems.map((item, index) => (
                        <div key={item.id} className="row g-2 align-items-center mb-2 pb-2 border-bottom">
                            <div className="col-md-1">
                                <span className="badge bg-secondary">#{index + 1}</span>
                            </div>
                            <div className="col-md-8">
                                <input type="text" className="form-control form-control-sm" placeholder="Tool Name" value={item.text} onChange={e => handleListItemChange(index, 'text', e.target.value)} />
                            </div>
                            <div className="col-md-3 text-end">
                                <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => removeListItem(index)}><Trash2 size={16} /></button>
                            </div>
                        </div>
                    ))}
                    {formData.listItems.length === 0 && <p className="text-center text-muted py-3">No items found.</p>}
                </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default AdminFeatureTwo;